import { createFileRoute, notFound } from "@tanstack/react-router";

import { SpecDetailsPage, SpecDetailsPending } from "@/components/specifications/spec-details-page";
import { specificationSummaryQueryOptions, useSpecificationSummary } from "@/hooks/specifications";
import { specDetailsSearchSchema } from "@/api/contracts/specifications";
import { parseRouteId } from "@/lib/route-paths";
import { SPEC_PAGE_TITLE, pageTitle } from "@/lib/spec-display";
import { specSearchNavigation } from "@/lib/spec-lines-display";

import { SPEC_LAYOUT_ROUTE_ID } from "../_spec";

// One version's details page — the donor's `/specifications/:specId/versions/
// :versionId` (`spec-details-page.tsx`), above its lines: the banners, the
// breadcrumb, the heading, the metadata line and the notes (#694) — and its
// lines (#695): the toolbar in the frame's header stack, the body beneath it,
// and the read-only item dialog the rows open.
//
// **The loader answers a bad id, never the component.** An id that is not one,
// a spec the read says is not there, or a version that is not this spec's all
// throw Page not found from here — a `notFound()` thrown from render loops
// until the crash boundary (the catalog edit route's note). It is targeted at
// the spec layout route, so the 404 replaces the band and renders beneath the
// app header (#680 Q3b, a named break: the donor draws an empty header over a
// request loop for an unknown id, and spec A's name over spec B's lines for a
// foreign one).
//
// **A read that fails is not a bad id** (#554 D4 (i)): the donor draws its
// island over whatever its reads left, with its permanent snackbar
// (`SpecDetailsIsland.tsx:289-291`), so the loader hands the page no summary
// and the page draws its blank face — the item route's precedent, "the
// donor's snackbar over a page that still draws" (`isStaleItemLink`). A spec's
// version cannot be checked without the read, so a foreign version id under a
// failed read draws that version's lines, as the donor's does.

export const Route = createFileRoute(
  "/_authed/_chrome/specifications/_spec/$specId/versions/$versionId",
)({
  // **No `remountDeps`**: the donor's route has no key, so a version switch
  // keeps its island mounted — the changes switch, the expansion map and the
  // search box's term carry over, and the new version's lines load under the
  // search skeleton beneath a live header (#695). The notes are the spec's,
  // so their disclosure carries over too (`SpecificationNotes.tsx:18`); the
  // banner's dismissal is keyed on the version and pointers, and so comes back
  // as the donor's does.
  //
  // `?specSearch=` (the line search) and `?lineId=&selectionId=` (the Over
  // Target popover's deep link) — read by the lines body, never by the loader:
  // a search is a new lines query, not a new page.
  validateSearch: specDetailsSearchSchema,
  // Every arrival leaves the scroll region to the browser, as the donor's
  // does (#714). A switch between two versions of the spec — the dropdown's
  // push, Back, Forward, a search — keeps the region mounted: its offset clamps
  // under the lines' search skeleton and anchors back when they land. Any other
  // arrival mounts a new region at the top. The router's restoration would
  // carry the previous page's offset over, or put back the entry's own on Back
  // and Forward. The window glides to the top on each arrival at a new path,
  // as the donor's layout scrolls it (@/lib/router-scroll).
  staticData: { leavesScrollToBrowser: true },
  // Back and Forward keep the page up only while the router still holds the
  // version they return to: a match it has left, forgotten at the next commit
  // once older than this (five minutes by default), is a first arrival again —
  // the island skeleton, and a new region at the top. The donor keeps its page
  // up however long the reader stayed, so this route's matches are kept for
  // the session; re-entry still reloads in the background, as any does.
  gcTime: Infinity,
  loader: async ({ context, params }) => {
    const specId = parseRouteId(params.specId);
    const versionId = parseRouteId(params.versionId);
    if (specId === null || versionId === null) throw notFound({ routeId: SPEC_LAYOUT_ROUTE_ID });
    let summary;
    try {
      summary = await context.queryClient.ensureQueryData(specificationSummaryQueryOptions(specId));
    } catch {
      // A failed read, whatever failed it. An expired session is still
      // answered by the query cache's own interceptor, which raises the
      // session overlay over this page (`router.tsx`, `unauthorizedInterceptor`).
      return { summary: null };
    }
    const version = summary?.versions.find((candidate) => candidate.id === versionId);
    if (!summary || !version) {
      throw notFound({ routeId: SPEC_LAYOUT_ROUTE_ID });
    }
    return { summary };
  },
  // `Specification | <name> | <division>` — the donor's `useSetPageTitle`
  // without its product suffix — and its bare `Specification` with no name to
  // add, which is a failed read here. The pending frames' `Specification` is
  // the pending face's own, since this head waits on the loader; the 404's
  // title is the layout's.
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData?.summary ? pageTitle(loaderData.summary) : SPEC_PAGE_TITLE }],
  }),
  // The donor's island skeleton — the header's bars and five groups — while
  // the summary is on its way, as the page keeps it up until the first lines
  // page lands (#695); at once and for no minimum, as the donor swaps it in and out on its loading flag,
  // beneath a band that is already up. The router's defaults (1s before it
  // shows, then 500ms at least) would keep the previous page up instead.
  pendingComponent: SpecDetailsPending,
  pendingMs: 0,
  pendingMinMs: 0,
  component: SpecDetailsRoute,
});

function SpecDetailsRoute() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const loaded = Route.useLoaderData();
  const specId = parseRouteId(params.specId);
  const versionId = parseRouteId(params.versionId);
  // The live query rather than the loader's snapshot, so a refetch — a
  // mutation's corrected summary (ADR-0014) — reaches the page. **Not retried
  // on mount** after the loader's read failed: the donor asks once, and its
  // snackbar says to refresh.
  const live = useSpecificationSummary(specId, { retryOnMount: false }).data;
  const summary = live === undefined ? loaded.summary : live;
  // The loader has answered a bad id; this only narrows.
  if (specId === null || versionId === null) return null;
  const onSearchChange = (term: string) => void navigate(specSearchNavigation(term));
  if (summary === null) {
    // A refetch that says the spec is gone leaves nothing to draw, as below.
    if (live === null) return null;
    return (
      <SpecDetailsPage
        read={{ summary: null, specId, versionId }}
        search={search}
        onSearchChange={onSearchChange}
      />
    );
  }
  const version = summary.versions.find((candidate) => candidate.id === versionId);
  // Only a refetch can take the version away from under a rendered page; the
  // loader has already answered every URL that arrives without one.
  if (!version) return null;
  return (
    <SpecDetailsPage read={{ summary, version }} search={search} onSearchChange={onSearchChange} />
  );
}
